import { SensorNode } from '@app/shared/models/sensor-node';

export class LightingConfiguration {
  nodeId: number;
  zone: number;
  zoneUpdatedAt: Date;
  maxLightLevel: number;
  maxLightLevelUpdatedAt: Date;
  lowLightLevel: number;
  lowLightLevelUpdatedAt: Date;
  minLightLevel: number;
  minLightLevelUpdatedAt: Date;
  dwellTime: number;
  dwellTimeUpdatedAt: Date;
  lowLightTime: number;
  lowLightTimeUpdatedAt: Date;
  updateStatus: string;
  scene: number;
  sceneUpdatedAt: Date;
  motionIdleMode: boolean;
  motionIdleModeUpdatedAt: Date;
  motionSensitivity: number;
  motionSensitivityUpdatedAt: Date;
  personality: number;
  personalityUpdatedAt: Date;
  irEnable: boolean;
  irEnableUpdatedAt: Date;

  constructor(
    nodeId: number,
    zone?: number,
    scene?: number,
    maxLightLevel?: number,
    lowLightLevel?: number,
    minLightLevel?: number,
    dwellTime?: number,
    lowLightTime?: number,
    updateStatus?: string,
    motionIdleMode?: boolean,
    motionSensitivity?: number,
    personality?: number,
    irEnable?: boolean
  ) {
    this.nodeId = nodeId;
    this.zone = zone;
    this.scene = scene;
    this.maxLightLevel = maxLightLevel;
    this.lowLightLevel = lowLightLevel;
    this.minLightLevel = minLightLevel;
    this.dwellTime = dwellTime;
    this.lowLightTime = lowLightTime;
    this.updateStatus = updateStatus;
    this.motionIdleMode = motionIdleMode ?? false;
    this.motionSensitivity = motionSensitivity ?? 0;
    this.personality = personality ?? 0;
    this.irEnable = irEnable ?? false;
  }

  public static isZoneSupportedNode(nodeType: string): boolean {
    return nodeType === SensorNode.SN3_NODE_TYPE || nodeType === SensorNode.SN3_UPGRADE_NODE_TYPE;
  }

  public static isMotionSupportedNode(nodeType: string): boolean {
    return nodeType === SensorNode.SN3_NODE_TYPE || nodeType === SensorNode.SN3_UPGRADE_NODE_TYPE;
  }

  public static isIrSupportedNode(nodeType: string): boolean {
    return nodeType === SensorNode.SN3_NODE_TYPE || nodeType === SensorNode.SN3_UPGRADE_NODE_TYPE;
  }

  public static fromResponse(response: any): LightingConfiguration {
    if (response == null) {
      response = {};
    }
    const config = new LightingConfiguration(
      response.nodeId,
      response.zone,
      response.scene,
      response.maxLightLevel,
      response.lowLightLevel,
      response.minLightLevel,
      response.dwellTime,
      response.lowLightTime,
      response.updateStatus,
      response.motionIdleMode,
      response.motionSensitivity,
      response.personality,
      response.irEnable
    );
    config.convertTimestampsToDates(response);
    return config;
  }

  convertTimestampsToDates(response: any): void {
    this.zoneUpdatedAt = new Date(response.zoneUpdatedAt);
    this.maxLightLevelUpdatedAt = new Date(response.maxLightLevelUpdatedAt);
    this.lowLightLevelUpdatedAt = new Date(response.lowLightLevelUpdatedAt);
    this.minLightLevelUpdatedAt = new Date(response.minLightLevelUpdatedAt);
    this.dwellTimeUpdatedAt = new Date(response.dwellTimeUpdatedAt);
    this.lowLightTimeUpdatedAt = new Date(response.lowLightTimeUpdatedAt);
    this.sceneUpdatedAt = new Date(response.sceneUpdatedAt);
    this.personalityUpdatedAt = new Date(response.personalityUpdatedAt);
    this.motionSensitivityUpdatedAt = new Date(response.motionSensitivityUpdatedAt);
    this.motionIdleModeUpdatedAt = new Date(response.motionIdleModeUpdatedAt);
    this.irEnableUpdatedAt = new Date(response.irEnableUpdatedAt);
  }

  findLastUpdatedTime(): Date {
    let lastTime = new Date(0);
    if (this.zoneUpdatedAt > lastTime) {
      lastTime = this.zoneUpdatedAt;
    }
    if (this.maxLightLevelUpdatedAt > lastTime) {
      lastTime = this.maxLightLevelUpdatedAt;
    }

    if (this.minLightLevelUpdatedAt > lastTime) {
      lastTime = this.minLightLevelUpdatedAt;
    }

    if (this.lowLightLevelUpdatedAt > lastTime) {
      lastTime = this.lowLightLevelUpdatedAt;
    }

    if (this.dwellTimeUpdatedAt > lastTime) {
      lastTime = this.dwellTimeUpdatedAt;
    }
    if (this.lowLightTimeUpdatedAt > lastTime) {
      lastTime = this.lowLightTimeUpdatedAt;
    }
    if (this.sceneUpdatedAt > lastTime) {
      lastTime = this.sceneUpdatedAt;
    }
    if (this.personalityUpdatedAt > lastTime) {
      lastTime = this.personalityUpdatedAt;
    }
    if (this.motionSensitivityUpdatedAt > lastTime) {
      lastTime = this.motionSensitivityUpdatedAt;
    }
    if (this.motionIdleModeUpdatedAt > lastTime) {
      lastTime = this.motionIdleModeUpdatedAt;
    }
    if (this.irEnableUpdatedAt > lastTime) {
      lastTime = this.irEnableUpdatedAt;
    }
    return lastTime;
  }

  isPartiallyUpdated(lastTime: Date, nodeType: string): boolean {
    return (
      (LightingConfiguration.isZoneSupportedNode(nodeType) && this.zoneUpdatedAt > lastTime) ||
      this.maxLightLevelUpdatedAt > lastTime ||
      this.minLightLevelUpdatedAt > lastTime ||
      this.lowLightLevelUpdatedAt > lastTime ||
      this.dwellTimeUpdatedAt > lastTime ||
      this.lowLightLevelUpdatedAt > lastTime ||
      this.sceneUpdatedAt > lastTime ||
      this.personalityUpdatedAt > lastTime ||
      (LightingConfiguration.isMotionSupportedNode(nodeType) && this.motionSensitivityUpdatedAt > lastTime) ||
      (LightingConfiguration.isMotionSupportedNode(nodeType) && this.motionIdleModeUpdatedAt > lastTime) ||
      (LightingConfiguration.isIrSupportedNode(nodeType) && this.irEnableUpdatedAt > lastTime)
    );
  }
}
